// Mock data for CLIMATECO2RE website

export const mockData = {
  hero: {
    title: 'Blockchain-Backed AI for a Net-Zero Future',
    subtitle: 'Transform your sustainability journey with transparent, AI-powered ESG intelligence',
    stats: [
      { value: 'Scope 1–3', label: 'Full Emissions Coverage' },
      { value: 'Real-Time', label: 'ESG Tracking' },
      { value: 'Blockchain', label: 'Verified Data' },
      { value: 'AI-Powered', label: 'Carbon Insights' },
    ],
  },
  
  features: [
    {
      number: '01',
      title: 'Blockchain-Verified ESG Data',
      description: 'Immutable, transparent tracking of environmental metrics with complete audit trails. Every data point is cryptographically secured and time-stamped.',
      icon: 'Lock',
      benefits: ['Tamper-proof records', 'Complete transparency', 'Regulatory compliance'],
    },
    {
      number: '02',
      title: 'AI-Powered Carbon Intelligence',
      description: 'Advanced machine learning algorithms analyze your carbon footprint in real-time, providing predictive insights and optimization recommendations.',
      icon: 'Brain',
      benefits: ['Predictive analytics', 'Smart recommendations', 'Automated reporting'],
    },
    {
      number: '03',
      title: 'Real-Time ESG Dashboard',
      description: 'Monitor all sustainability metrics in one unified platform. Track carbon credits, offset programs, and compliance status instantly.',
      icon: 'BarChart3',
      benefits: ['Live monitoring', 'Custom metrics', 'Multi-source integration'],
    },
  ],

  manifesto: [
    {
      number: '01',
      title: 'Transparency First',
      description: 'We believe every carbon credit, every ESG metric, and every sustainability claim should be verifiable on the blockchain. No greenwashing, only truth.',
    },
    {
      number: '02',
      title: 'AI-Driven Action',
      description: 'Data without insights is noise. Our AI transforms your sustainability data into actionable intelligence that drives real climate impact.',
    },
    {
      number: '03',
      title: 'Net-Zero Commitment',
      description: 'Every organization can reach net-zero. We provide the tools, intelligence, and verification to make it happen faster and more credibly.',
    },
  ],

  solutions: [
    {
      title: 'Enterprise ESG Management',
      description: 'Complete sustainability reporting platform for large organizations',
      features: ['Multi-entity reporting', 'Scope 1, 2, 3 emissions', 'Custom frameworks', 'API integration'],
      industry: 'Enterprises',
    },
    {
      title: 'Carbon Credit Trading',
      description: 'Blockchain-based marketplace for verified carbon credits',
      features: ['Instant verification', 'Smart contracts', 'Price transparency', 'Fraud prevention'],
      industry: 'Carbon Markets',
    },
    {
      title: 'Supply Chain Tracking',
      description: 'End-to-end visibility of product carbon footprints',
      features: ['Product lifecycle', 'Supplier tracking', 'Impact scoring', 'Consumer transparency'],
      industry: 'Manufacturing',
    },
    {
      title: 'Regulatory Compliance',
      description: 'Automated compliance with global ESG regulations',
      features: ['CSRD ready', 'TCFD aligned', 'GRI standards', 'Auto-reporting'],
      industry: 'All Sectors',
    },
  ],

  team: [
    {
      name: 'Maryam Gull',
      role: 'Founder',
      bio: 'Environmental engineer specialized in ESG reporting and sustainability compliance. Educated at the University of Engineering and Technology, Lahore.',
      image: null,
      linkedin: 'https://www.linkedin.com/in/maryamgull-',
    },
    {
      name: 'Ayesha Gull',
      role: 'Chief Marketing Officer',
      bio: 'Brand communications and creative strategist with experience at CERN. Educated at Beaconhouse National University, Lahore.',
      image: null,
      linkedin: 'https://www.linkedin.com/in/ayesha-gull-b034bb185/',
    },
    {
      name: 'Zain Haider',
      role: 'Chief Growth Officer',
      bio: 'B2B sales and market development leader with prior experience at Lotte and Nestlé. Educated at COMSATS University Islamabad.',
      image: null,
      linkedin: 'https://www.linkedin.com/in/syed-zain-haider-a6078418b/',
    },
    {
      name: 'Atiqah binte Fayyaz',
      role: 'Head of Sustainability',
      bio: 'Climate change and carbon market expert with work experience at WWF Pakistan. Researcher focused on green financing. Educated at the National University of Sciences and Technology (NUST).',
      image: null,
      linkedin: 'https://www.linkedin.com/in/atiqah-binte-fayyaz-aa3b051b9/',
    },
  ],

  blog: [
    {
      id: 1,
      title: 'The Future of Carbon Markets: How Blockchain is Revolutionizing Climate Action',
      excerpt: 'Exploring how distributed ledger technology brings unprecedented transparency to carbon credit trading...',
      category: 'Blockchain',
      date: '2025-01-15',
      readTime: '8 min read',
      author: 'Ayesha Gull',
      image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80&auto=format&fit=crop',
    },
    {
      id: 2,
      title: 'AI and ESG: From Data Overload to Actionable Insights',
      excerpt: 'How machine learning is transforming the way companies understand and act on sustainability data...',
      category: 'AI & Technology',
      date: '2025-01-10',
      readTime: '6 min read',
      author: 'Zain Haider',
      image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&q=80&auto=format&fit=crop',
    },
    {
      id: 3,
      title: 'CSRD Compliance: A Complete Guide for 2025',
      excerpt: 'Everything you need to know about the Corporate Sustainability Reporting Directive and how to prepare...',
      category: 'Compliance',
      date: '2025-01-05',
      readTime: '12 min read',
      author: 'Atiqah binte Fayyaz',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80&auto=format&fit=crop',
    },
    {
      id: 4,
      title: 'Scope 3 Emissions: Tackling the Hardest Part of Your Carbon Footprint',
      excerpt: 'Supply chain emissions often account for over 70% of a company\'s footprint. Here\'s how AI and blockchain make them measurable and verifiable...',
      category: 'ESG Insights',
      date: '2024-12-28',
      readTime: '9 min read',
      author: 'Maryam Gull',
      image: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=800&q=80&auto=format&fit=crop',
    },
    {
      id: 5,
      title: 'Smart Contracts for Carbon Credits: Ending Double-Counting Forever',
      excerpt: 'How self-executing smart contracts on the blockchain eliminate fraud and double-counting in voluntary carbon markets...',
      category: 'Blockchain',
      date: '2024-12-20',
      readTime: '7 min read',
      author: 'Ayesha Gull',
      image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=800&q=80&auto=format&fit=crop',
    },
    {
      id: 6,
      title: 'Predictive Sustainability: Using AI to Forecast Your Net-Zero Trajectory',
      excerpt: 'Machine learning models can now project your emissions years ahead, helping you course-correct before targets slip out of reach...',
      category: 'AI & Technology',
      date: '2024-12-12',
      readTime: '10 min read',
      author: 'Zain Haider',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80&auto=format&fit=crop',
    },
    {
      id: 7,
      title: 'From Greenwashing to Green-Proof: Building Trust with Verifiable ESG Data',
      excerpt: 'Regulators and investors are cracking down on unsubstantiated claims. Learn how immutable data turns sustainability promises into proof...',
      category: 'ESG Insights',
      date: '2024-12-05',
      readTime: '8 min read',
      author: 'Atiqah binte Fayyaz',
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80&auto=format&fit=crop',
    },
    {
      id: 8,
      title: 'The Rise of Digital MRV: Measurement, Reporting and Verification Reinvented',
      excerpt: 'Traditional MRV is slow, manual, and error-prone. Discover how blockchain-backed digital MRV delivers real-time, audit-ready reporting...',
      category: 'AI & Technology',
      date: '2024-11-27',
      readTime: '11 min read',
      author: 'Maryam Gull',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80&auto=format&fit=crop',
    },
    {
      id: 9,
      title: 'TCFD to ISSB: Navigating the New Global Climate Disclosure Standards',
      excerpt: 'The reporting landscape is consolidating fast. A practical roadmap for aligning your disclosures with ISSB\'s IFRS S1 and S2 standards...',
      category: 'Compliance',
      date: '2024-11-18',
      readTime: '13 min read',
      author: 'Atiqah binte Fayyaz',
      image: 'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800&q=80&auto=format&fit=crop',
    },
  ],

  testimonials: [
    {
      quote: 'CLIMATECO2RE transformed our ESG reporting from a quarterly headache to a real-time strategic advantage.',
      author: 'Lisa Anderson',
      role: 'Chief Sustainability Officer',
      company: 'Global Manufacturing Corp',
    },
    {
      quote: 'The blockchain verification gives our stakeholders complete confidence in our carbon neutrality claims.',
      author: 'Michael Zhang',
      role: 'VP of Operations',
      company: 'TechForward Inc',
    },
    {
      quote: 'Finally, an ESG platform that speaks both the language of compliance and the language of innovation.',
      author: 'Emma Thompson',
      role: 'Head of Risk & Compliance',
      company: 'Financial Services Leader',
    },
  ],

  faq: [
    {
      question: 'How does blockchain ensure data integrity?',
      answer: 'Every ESG data point is hashed and stored on an immutable distributed ledger. This creates a permanent, tamper-proof record with complete audit trails.',
    },
    {
      question: 'What AI models power your platform?',
      answer: 'We use proprietary machine learning models trained on millions of data points, including predictive carbon modeling, anomaly detection, and optimization algorithms.',
    },
    {
      question: 'Which ESG frameworks do you support?',
      answer: 'We support GRI, SASB, TCFD, CDP, CSRD, and custom frameworks. Our platform adapts to your specific reporting requirements.',
    },
    {
      question: 'How quickly can we get started?',
      answer: 'Most enterprises are up and running within 2-4 weeks, including data integration, team training, and initial reporting setup.',
    },
  ],

  pricing: [
    {
      tier: 'Starter',
      price: '$2,999',
      period: '/month',
      description: 'Perfect for growing companies starting their ESG journey',
      features: [
        'Up to 3 facilities',
        'Basic ESG metrics',
        'Monthly reporting',
        'Email support',
        'Standard integrations',
      ],
    },
    {
      tier: 'Professional',
      price: '$7,999',
      period: '/month',
      description: 'For established enterprises with complex reporting needs',
      features: [
        'Unlimited facilities',
        'Advanced AI analytics',
        'Real-time reporting',
        'Priority support',
        'Custom integrations',
        'API access',
        'White-label options',
      ],
      popular: true,
    },
    {
      tier: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'Tailored solutions for large organizations',
      features: [
        'Everything in Professional',
        'Dedicated account manager',
        'Custom AI models',
        'On-premise deployment',
        'Advanced security',
        'SLA guarantees',
        'Custom development',
      ],
    },
  ],
};